const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const formatPackageMoney = (value, currency = "INR") => {
  const amount = toNumber(value, 0);
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency || "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `₹${Math.round(amount).toLocaleString("en-IN")}`;
  }
};

export const formatPackageDate = (value = "") => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const date = new Date(raw.length === 10 ? `${raw}T00:00:00` : raw);
  if (Number.isNaN(date.getTime())) return raw;
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

export const formatPackageGuests = (guestMix = {}) => {
  const adults = toNumber(guestMix?.adults ?? guestMix?.adult, 0);
  const children = toNumber(guestMix?.children ?? guestMix?.child, 0);
  const infants = toNumber(guestMix?.infants ?? guestMix?.infant, 0);
  const parts = [];
  if (adults) parts.push(`${adults} adult${adults === 1 ? "" : "s"}`);
  if (children) parts.push(`${children} child${children === 1 ? "" : "ren"}`);
  if (infants) parts.push(`${infants} infant${infants === 1 ? "" : "s"}`);
  return parts.join(", ");
};

export const resolvePackageContext = (source = {}) => {
  const direct = source?.packageContext;
  const metadata = source?.metadata || {};
  const options = source?.options || {};
  const quoteMeta = source?.quoteMeta || {};
  const context =
    direct && typeof direct === "object"
      ? direct
      : metadata?.packageContext && typeof metadata.packageContext === "object"
        ? metadata.packageContext
        : options?.packageContext && typeof options.packageContext === "object"
          ? options.packageContext
          : quoteMeta?.packageContext && typeof quoteMeta.packageContext === "object"
            ? quoteMeta.packageContext
            : null;
  const packageQuoteCode =
    context?.packageQuoteCode ||
    source?.packageQuoteCode ||
    metadata?.packageQuoteCode ||
    quoteMeta?.packageQuoteCode ||
    "";
  if (!context && !packageQuoteCode) return null;
  return {
    ...(context || {}),
    packageQuoteCode,
    packageRef: context?.packageRef || source?.packageRef || metadata?.packageRef || "",
    packageSlug: context?.packageSlug || source?.packageSlug || metadata?.packageSlug || "",
    packageTitle: context?.packageTitle || source?.packageTitle || metadata?.packageTitle || source?.subtitle || "Package",
    packageFamilyKey: context?.packageFamilyKey || source?.packageFamilyKey || metadata?.packageFamilyKey || "",
    packageVariantKey: context?.packageVariantKey || source?.packageVariantKey || metadata?.packageVariantKey || "",
    packageVariantLabel:
      context?.packageVariantLabel
      || source?.packageVariantLabel
      || metadata?.packageVariantLabel
      || "",
    itemId: context?.itemId || source?.packageItemId || metadata?.packageItemId || "",
    dayIndex: toNumber(context?.dayIndex ?? source?.packageDayIndex ?? metadata?.packageDayIndex, 0),
    startDate: context?.startDate || source?.packageStartDate || metadata?.packageStartDate || "",
    endDate: context?.endDate || source?.packageEndDate || metadata?.packageEndDate || "",
    guests: context?.guests || source?.packageGuests || metadata?.packageGuests || source?.guestMix || options?.guestMix || null,
    itemGuests: context?.itemGuests || source?.packageItemGuests || metadata?.packageItemGuests || source?.guestMix || options?.itemGuests || options?.guestMix || null,
    plannerState: context?.plannerState || source?.plannerState || metadata?.plannerState || "",
    packagePricing:
      context?.packagePricing
      || source?.packagePricing
      || metadata?.packagePricing
      || quoteMeta?.packagePricing
      || null,
  };
};

export const isPackageItem = (source = {}) => Boolean(resolvePackageContext(source));

export const resolveCartItemSubtotal = (item = {}) => {
  if (item?.serverTotals) {
    if (item.serverTotals.authoritative !== true || item.serverTotals.status !== "verified") return 0;
    const amountPaise = Number(item.serverTotals.amountPaise);
    if (Number.isFinite(amountPaise) && amountPaise >= 0) return amountPaise / 100;
  }
  const totals = item?.pricingSnapshot || item?.quote?.totals || item?.options?.totals || {};
  const rawTotal =
    totals.grandTotal ??
    totals.estimatedPayable ??
    totals.totalAmount ??
    totals.serverTotal ??
    null;
  const parsedTotal = Number(rawTotal);
  if (Number.isFinite(parsedTotal)) return Math.max(0, parsedTotal);
  const unitPrice = toNumber(item?.price, 0);
  const quantity = Math.max(1, toNumber(item?.quantity, 1));
  return unitPrice * quantity;
};

export const productLabelFromType = (type = "") => {
  const value = String(type || "").toLowerCase();
  if (value.includes("room") || value.includes("stay") || value.includes("night")) return "Stay";
  if (value.includes("cruise")) return "Cruise";
  if (value.includes("trek")) return "Trek";
  if (value.includes("safari") || value.includes("jungle")) return "Safari";
  return "Item";
};

export const productIconFromType = (type = "") => {
  const value = String(type || "").toLowerCase();
  if (value.includes("room") || value.includes("stay") || value.includes("night")) return "fa-solid fa-bed";
  if (value.includes("cruise")) return "fa-solid fa-ship";
  if (value.includes("trek")) return "fa-solid fa-person-hiking";
  if (value.includes("safari") || value.includes("jungle")) return "fa-solid fa-binoculars";
  return "fa-solid fa-map-location-dot";
};

const packageGroupKey = (context = {}) =>
  context.packageQuoteCode || `${context.packageSlug || "package"}:${context.startDate || ""}`;

export const buildPackageItemGroups = (items = []) => {
  const groups = new Map();
  const entries = [];

  (Array.isArray(items) ? items : []).forEach((item) => {
    const context = resolvePackageContext(item);
    if (!context) {
      entries.push({ type: "item", key: item?.uid || item?.key || item?.id, item });
      return;
    }
    const key = packageGroupKey(context);
    if (!groups.has(key)) {
      const group = {
        type: "package",
        key,
        context,
        title: context.packageTitle || item?.subtitle || "Package",
        packageQuoteCode: context.packageQuoteCode || "",
        packageFamilyKey: context.packageFamilyKey || "",
        packageVariantKey: context.packageVariantKey || "",
        packageVariantLabel: context.packageVariantLabel || "",
        startDate: context.startDate || "",
        endDate: context.endDate || "",
        guests: context.guests || null,
        items: [],
        total: 0,
        pricing: context.packagePricing || null,
      };
      groups.set(key, group);
      entries.push(group);
    }
    const group = groups.get(key);
    if (!group.pricing && context.packagePricing) group.pricing = context.packagePricing;
    const price = resolveCartItemSubtotal(item);
    group.total += price;
    group.items.push({
      item,
      context,
      price,
      dayIndex: context.dayIndex || 0,
      productType: item?.experienceType || item?.productType || item?.kind || "",
      title: item?.title || "Package item",
      date: item?.options?.checkIn || item?.options?.date || item?.travelDate || context.startDate || "",
    });
  });

  entries.forEach((entry) => {
    if (entry.type !== "package") return;
    entry.items.sort((left, right) => {
      const dayDelta = (left.dayIndex || 0) - (right.dayIndex || 0);
      if (dayDelta) return dayDelta;
      return String(left.title || "").localeCompare(String(right.title || ""));
    });
  });

  return entries;
};

export const buildPackageCartSummary = (items = []) => {
  const entries = buildPackageItemGroups(items);
  return entries.reduce(
    (summary, entry) => {
      if (entry.type === "package") {
        summary.count += 1;
        summary.subtotal += entry.total;
        summary.packageCount += 1;
        summary.packageItemCount += entry.items.length;
        summary.rawItemCount += entry.items.length;
        return summary;
      }
      const quantity = Math.max(1, toNumber(entry.item?.quantity, 1));
      summary.count += 1;
      summary.rawItemCount += quantity;
      summary.subtotal += resolveCartItemSubtotal(entry.item);
      return summary;
    },
    {
      count: 0,
      subtotal: 0,
      packageCount: 0,
      packageItemCount: 0,
      rawItemCount: 0,
    },
  );
};
